import dayjs from 'dayjs';

import { siteInfoHeightReceived, windowResize, newConsultation, pushSnackbar } from 'FRONTEND/actions/app';
import {
  APP_INIT,
  NEW_CONSULTATION,
  SITE_INFO_HEIGHT_RECEIVED,
} from 'FRONTEND/actions/constants';
import {
  listRequest as listAppointmentsRequest,
} from 'FRONTEND/reducers/appointmentSlice';
import { editSettings, listSettings } from 'FRONTEND/reducers/expertSlice';
import { videoconferenceReady, extendRightPanel, reduceRightPanel, showRightPanel, endVideoConference, joinConfRoom } from 'FRONTEND/reducers/rightPanelSlice';
import { userUpdate } from 'FRONTEND/reducers/userSlice';
import { VIDEO_CONSULTATION, VIDEO_SPECIALIST_ASSESSMENT, OTHER, EXPERT } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { desktopNotify } from 'FRONTEND/utils/notify';
import { getParentWindow, hasParentWindow } from 'FRONTEND/utils/parentWindow';
import { setupPjaxFormSpecificListeners } from 'FRONTEND/utils/setupPjaxFormSpecificListeners';
import { getDurationSettingKey } from 'FRONTEND/utils/stringFormatting';
import { RTC_IFRAME_ID } from 'LEGACY/constants';
import { debounce } from 'LEGACY/eventDelaying';
import { logOut, rtaSettingsIframeInit } from 'LEGACY/init';

const appMiddleware = (store) => (next) => async(action) => {
  if ((action.type?.endsWith('_ERROR') || action.toString()?.endsWith('Error')) && action.payload?.error === '401') {
    $('#user_inactivity_modal').modal({
      backdrop: 'static',
      keyboard: false,
    });
    logOut();
  }
  const rtcIframe = document.getElementById(RTC_IFRAME_ID);

  switch (action.type) {
    case APP_INIT: {
      next(action);
      rtcIframe?.addEventListener('load', () => {
        store.dispatch(windowResize());
      });
      window.addEventListener('resize', debounce(() => {
        store.dispatch(windowResize());
      }));

      // setup form specific event listeners
      setupPjaxFormSpecificListeners(store.dispatch);

      // Managing cross-origin communication messages (from RTA, AT and RTC).
      window.addEventListener('message', (event) => {
        if (!event.data || !event.data.type) {
          return;
        }
        const messageType = event.data.type;
        switch (messageType) {
          case 'parsys_rtc_call_ended_conf':
            store.dispatch(endVideoConference());
            break;
          case 'cloud_settings_update': {
            const settings = {
              [getDurationSettingKey(VIDEO_CONSULTATION)]: event.data.teleconsultation_duration,
              [getDurationSettingKey(VIDEO_SPECIALIST_ASSESSMENT)]: event.data.telestaff_duration,
              [getDurationSettingKey(OTHER)]: event.data.other_appointment_duration,
            };
            store.dispatch(editSettings({ expertId: window.userId, settings, showSnackbar: true }));
            break;
          }
          case 'user_info_update':
            store.dispatch(userUpdate(event.data));
            break;
          case 'user_locale_update':
            store.dispatch(userUpdate({ locale: event.data.locale }));
            break;
          case 'parsys_rtc_extend':
            store.dispatch(extendRightPanel());
            store.dispatch(showRightPanel());
            break;
          case 'parsys_rtc_reduce':
            store.dispatch(reduceRightPanel());
            break;
          case 'parsys_rtc_listening':
            store.dispatch(videoconferenceReady({ videoConferenceUrl: event.origin }));
            window?.Notification?.requestPermission().then();
            break;
          case 'parsys_notification':
            desktopNotify(event.data.notification);
            break;
          case 'parsys_site_info':
            store.dispatch(siteInfoHeightReceived({ height: event.data.height }));
            break;
          case 'newConsultation':
            store.dispatch(newConsultation({ id: event.data.body?.id }));
            break;
          case 'refreshAppointment': {
            const isUnrestrictedMode = store.getState().calendar.isUnrestrictedMode;
            // If in calendar, refresh appointments for the current week.
            if (store.getState().calendar.isMounted) {
              const start = new Date(store.getState().calendar.currentDay);
              store.dispatch(listAppointmentsRequest({ actorId: store.getState().user.id, actorType: EXPERT, startDate: start.toString(), endDate: dayjs(start).endOf('week').toString() }));
              // If in appointments list, refresh appointments.
            } else if (store.getState().calendar.isAppointmentListMounted) {
              store.dispatch(listAppointmentsRequest({ actorId: store.getState().user.id, isUnrestricted: isUnrestrictedMode, actorType: EXPERT, startDate: dayjs() }));
            }
            break;
          }
          case 'ping_for_readiness': {
            const rtaSettingsIframe = document.getElementById('user_settings-iframe');
            rtaSettingsIframe.contentWindow.postMessage({ type: 'cloud_ready' }, '*');
            break;
          }
          case 'data_pass_through': {
            if (event.data.isResponse) {
              if (hasParentWindow()) {
                getParentWindow().postMessage(event.data, '*');
              }
            } else {
              // Re-get the DOM element because it might not have been there when APP_INIT was dispatched.
              const rtcIframe = document.getElementById(RTC_IFRAME_ID);
              if (rtcIframe) {
                rtcIframe.contentWindow.postMessage(event.data, '*');
              } else {
                console.warn('Received data_pass_through with no RTC iframe to forward to.');
              }
            }
            break;
          }
          default:
        }
      });

      const rtaSettingsIframe = document.getElementById('user_settings-iframe');
      if (rtaSettingsIframe) {
        // Handle case where RTA pings Cloud but Cloud wasn't ready to listen.
        rtaSettingsIframe.contentWindow.postMessage({ type: 'cloud_ready' }, '*');
      }

      if (!store.getState().rightPanel.isVideoConferenceReady && rtcIframe) {
        rtcIframe.contentWindow.postMessage({ type: 'ping_for_readiness' }, '*');
      }
      return;
    }
    case userUpdate.toString(): {
      next(action);
      dayjs.locale(action.payload?.locale ?? store.getState().user.locale);
      if (rtcIframe) {
        rtcIframe.contentWindow.postMessage({
          type: 'user_locale_update',
          locale: action.payload?.locale ?? store.getState().user.locale,
        }, '*');
      }
      return;
    }

    case videoconferenceReady.toString():
    case joinConfRoom.toString():
      next(action);
      if (rtcIframe) {
        rtcIframe.contentWindow.postMessage({
          type: 'user_locale_update',
          locale: action.payload?.locale ?? store.getState().user.locale,
        }, '*');
      }
      return;

    case SITE_INFO_HEIGHT_RECEIVED: {
      next(action);
      const siteIframe = document.getElementById('breadcrumbs-site');
      if (Number.isInteger(action.payload.height) && action.payload.height > 0) {
        siteIframe.style.height = `${action.payload.height}px`;
      } else {
        siteIframe.classList.add('hidden');
      }
      return;
    }

    case NEW_CONSULTATION: {
      const consultationsTable = $('#consultations-list');
      const patientHistoryTable = $('#patient-history-list');
      if (consultationsTable.length) {
        window.consultationToHighlight = action.payload.id;
        consultationsTable.DataTable().draw();
      } else if (patientHistoryTable.length) {
        if (window.location.pathname === `/patients/${action.payload.patient_id}/history/`) {
          window.consultationToHighlight = action.payload.id;
          patientHistoryTable.DataTable().draw();
        }
      }
      return next(action);
    }
    case listSettings.fulfilled.toString(): {
      next(action);
      const userSettings = action.payload.settingsList.find(settings => settings.id === window.userId);
      rtaSettingsIframeInit(userSettings);
      return;
    }
    case editSettings.fulfilled.toString():
      next(action);
      if (action.meta.arg.showSnackbar) {
        store.dispatch(pushSnackbar({
          message: i18n._('Your settings have been updated.'),
          variant: 'success',
        }));
      }
      return;

    case editSettings.rejected.toString():
      next(action);
      if (action.meta.arg.showSnackbar) {
        store.dispatch(pushSnackbar({
          message: i18n._('There was an error while updating your settings. Please try again later or contact your administrator.'),
          variant: 'success',
        }));
      }
      return;

    default:
      next(action);
  }
};

export default appMiddleware;
